// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The French bundle — Québec French (01 §6, 08 §3).
//
// **fr-CA, not fr-FR**, and the difference shows up in more places than an accent. This product
// prices in Canadian dollars for Canadian readers, so the register is Canadian: *magasiner* rather
// than *faire du shopping*, *courriel* rather than *e-mail*, and *en tout temps* rather than *à
// tout moment*. Where Québec usage and France usage differ, Québec wins.
//
// **Typography is part of the translation.** French puts a space before `%`, `:`, `?` and `!`, and
// that space is non-breaking — a percentage that wraps onto the next line by itself is the kind of
// detail that makes a page read as machine-translated. The narrow no-break space (U+202F) is used
// before `%` and the ordinary no-break space (U+00A0) before the two-part punctuation, which is
// what the Office québécois de la langue française asks for and what `Intl` itself produces.
//
// **Quotation marks are guillemets** where any are needed, and apostrophes are typographic (’)
// rather than straight, matching the English bundle's own usage.
//
// The type annotation is what enforces completeness: this must be every key `EN` has, and the build
// fails if it is not.

import type { EN } from './en'

export const FR: Readonly<Record<keyof typeof EN, string>> = {
  // -------------------------------------------------------------------------
  // Forfaits et prix
  // -------------------------------------------------------------------------
  'plan.monthly': 'Mensuel',
  'plan.annual': 'Annuel',
  'plan.per.month': 'mois',
  'plan.per.year': 'an',
  'plan.note.cancel-any-time': 'Annulable en tout temps.',
  'plan.note.one-month-free': 'Un mois gratuit.',
  'plan.a-month': '{amount} par mois',
  'plan.save': 'économisez {percent} %',
  'plan.subscribe-title': 'S’abonner au forfait {plan} à {amount}',

  // -------------------------------------------------------------------------
  // Le mur payant
  // -------------------------------------------------------------------------
  'paywall.heading': 'Pro',
  'paywall.lead': 'Cela fait partie de Pro. Voici tout ce que l’abonnement ajoute d’autre.',
  'paywall.free-heading': 'D’abord, un compte gratuit',
  'paywall.free-lead':
    'Tout ceci est gratuit et ne demande aucun abonnement. Vous n’avez même pas besoin d’un compte pour jouer.',
  'paywall.subscribe': 'S’abonner',
  'paywall.opening': 'Ouverture du paiement…',
  'paywall.signin': 'Connectez-vous pour vous abonner.',
  'paywall.checkout-failed': 'Le paiement n’a pas pu être ouvert.',

  // -------------------------------------------------------------------------
  // Le panneau d’abonnement
  // -------------------------------------------------------------------------
  'subscription.heading': 'Pro',
  'subscription.checking': 'Vérification de votre abonnement…',
  'subscription.through': 'Abonnement souscrit par',
  'subscription.renews': 'Renouvellement',
  'subscription.never-expires': 'Jamais — cet abonnement n’expire pas',
  'subscription.manage': 'Gérer l’abonnement',
  'subscription.manage-title': 'Ouvrir Stripe pour modifier ou annuler votre abonnement',
  'subscription.opening': 'Ouverture…',
  'subscription.portal-failed': 'La gestion de la facturation n’a pas pu être ouverte.',
  'subscription.elsewhere-meta':
    'Cet abonnement est facturé par Meta. Modifiez-le ou annulez-le dans les paramètres de votre compte Meta.',
  'subscription.elsewhere-other': 'Cet abonnement n’est pas facturé par le web.',

  // -------------------------------------------------------------------------
  // Fournisseurs
  // -------------------------------------------------------------------------
  'provider.stripe': 'le web',
  'provider.meta': 'Meta',
  'provider.support': 'un octroi du soutien',
  'provider.system': 'ce compte',
  'provider.double-paying':
    'Vous êtes abonné par {providers}. Pro n’est pas doublé et aucun des deux abonnements n’est annulé automatiquement — vous pouvez mettre fin à l’un ou à l’autre là où vous l’avez souscrit.',
  'provider.and': 'et',

  // -------------------------------------------------------------------------
  // Ce que chaque forfait donne — les intitulés de 01 §6
  // -------------------------------------------------------------------------
  'capability.play-web': 'NEON NOVA dans le navigateur, sur ordinateur ou téléphone',
  'capability.local-scores': 'Meilleurs scores gardés dans ce navigateur',
  'capability.cloud-scores': 'Synchronisation des scores, historique des parties, reprises',
  'capability.leaderboards': 'Classements mondiaux et entre amis',
  'capability.hotseat': 'Parties à billes alternées, de 1 à 4 joueurs, sur le même appareil',
  'capability.online-games': 'Parties à billes alternées en ligne avec des amis',
  'capability.tournaments': 'Tournois hebdomadaires',
  'capability.community-machines': 'Jouer aux machines publiées par d’autres joueurs',
  'capability.builder-private': 'Créateur de machines — concevoir et jouer en privé',
  'capability.builder-publish': 'Créateur de machines — publier publiquement',
  'capability.builder-api': 'Créateur de machines — construire avec votre IA, et l’API du Créateur',
  'capability.headset': 'Casque RV et RM (WebXR et application native)',
  'capability.arcade': 'L’arcade virtuelle partagée, les avatars et la voix spatialisée',
  'capability.full-roster': 'Toutes les machines au-delà de NEON NOVA',

  // -------------------------------------------------------------------------
  // Refus Pro, par fonctionnalité
  // -------------------------------------------------------------------------
  'pro.community-machines':
    'Les machines créées par d’autres joueurs font partie de Pro. NEON NOVA est gratuite, toujours.',
  'pro.builder-private':
    'Le Créateur de machines fait partie de Pro. NEON NOVA se joue gratuitement sans lui.',
  'pro.builder-publish':
    'Publier une machine dans l’arcade fait partie de Pro. Vos brouillons restent les vôtres dans tous les cas.',
  'pro.builder-api': 'Construire des machines depuis vos propres outils et votre IA fait partie de Pro.',
  'pro.headset': 'Jouer dans un casque fait partie de Pro.',
  'pro.arcade': 'L’arcade partagée, les avatars et la voix font partie de Pro.',
  'pro.full-roster': 'Cette machine fait partie de Pro. NEON NOVA est gratuite, toujours.',
  'pro.default': 'Cela fait partie de Pro.',

  // -------------------------------------------------------------------------
  // Codes d’erreur — le vocabulaire fermé du contrat
  // -------------------------------------------------------------------------
  'error.bad_request': 'Quelque chose n’allait pas dans cette demande.',
  'error.unauthenticated': 'Connectez-vous pour faire cela.',
  'error.forbidden': 'Cela ne vous appartient pas.',
  'error.not_found': 'Cela ne se trouve pas ici.',
  'error.conflict': 'Quelque chose a changé pendant que vous travailliez. Jetez-y un autre coup d’œil.',
  'error.idempotency_mismatch': 'Cela ressemblait à la répétition d’autre chose. Réessayez.',
  'error.rate_limited': 'Trop de choses à la fois. Accordez-lui un instant.',
  'error.payload_too_large': 'C’est trop volumineux pour être envoyé.',
  'error.entitlement_required': 'Cela fait partie de Pro.',
  'error.moderation_blocked': 'Cela ne peut pas être publié tel quel.',
  'error.validation_failed': 'Cette machine ne peut pas encore être enregistrée.',
  'error.unsupported_core_version':
    'Cette partie a été jouée sur une version que le serveur ne peut pas rejouer.',
  'error.client_update_required':
    'Cette application n’est pas à jour. Rechargez la page pour obtenir la version actuelle.',
  'error.service_unavailable': 'Cela ne fonctionne pas en ce moment. Réessayez sous peu.',
  'error.internal_error': 'Quelque chose a mal tourné de notre côté.',
  'error.offline': 'Vous semblez être hors ligne.',
  'error.unknown': 'Cela n’a pas fonctionné.',

  // -------------------------------------------------------------------------
  // La liste des machines
  // -------------------------------------------------------------------------
  'arcade.loading': 'Chargement du reste de l’arcade…',
  'arcade.era.em': 'Électromécanique, années 1970',
  'arcade.era.ss': 'Électronique, années 1980',
  'arcade.era.dmd': 'Matrice de points, années 1990',
  'arcade.era.lcd': 'Moderne',
  'arcade.era.unknown': 'Pas encore téléchargée',
  'arcade.badge.pro': 'Pro',
  'arcade.badge.free-this-week': 'Gratuite cette semaine',
  'arcade.badge.free': 'Gratuite',
  'arcade.badge.try': 'Essai gratuit',
  'arcade.signin-title': 'Connectez-vous pour jouer à {title} — elle fait partie de Pro',
  'account.want.full-roster': 'Connectez-vous pour jouer à {title}. Le reste du catalogue fait partie de Pro ; NEON NOVA et la machine gratuite de la semaine ne demandent aucun compte.',
  'account.want.full-roster-plain': 'Connectez-vous pour jouer au reste du catalogue. Il fait partie de Pro ; NEON NOVA et la machine gratuite de la semaine ne demandent aucun compte.',
  'arcade.locked-title': '{title} fait partie de Pro',
  'arcade.play-title': 'Jouer à {title}',
  'arcade.devices': '{count} éléments',
  'arcade.build.name': 'Créer une machine',
  'arcade.build.meta': 'Concevez votre plateau, vos règles et vos sons',
  'arcade.build.title': 'Ouvrir le Créateur de machines',
  'arcade.build.title-pro':
    'Ouvrir le Créateur de machines — concevoir et essayer sont gratuits, conserver une machine fait partie de Pro',
  'arcade.guest-lede':
    'Une machine change chaque semaine et est gratuite pour tout le monde. Les autres font partie de Pro.',

  // -------------------------------------------------------------------------
  // Paramètres
  // -------------------------------------------------------------------------
  'settings.language': 'Langue',
  'settings.language-hint':
    'Change les mots de l’application. Les annonces vocales et l’afficheur d’une machine font partie de la machine et ne changent pas.',
  'settings.language.en': 'English',
  'settings.language.fr': 'Français',
  'settings.language-title': 'Lire cette application en {language}',
  'settings.currency': 'Devise',
  'settings.currency-hint':
    'La devise dans laquelle les prix sont affichés et facturés. Chacune a son propre prix; ce n’est pas une conversion.',
  'settings.currency.USD': 'Dollars américains',
  'settings.currency.CAD': 'Dollars canadiens',
  'settings.currency-title': 'Afficher et facturer les prix en {currency}',
  'settings.rating': 'Ce que l’arcade affiche',
  'settings.rating-hint':
    'Les auteurs classent eux-mêmes leurs machines. Cette application est classée 13 ans et plus; tout est affiché à moins que vous n’en demandiez moins.',
  'settings.rating.everyone': 'Tout public seulement',
  'settings.rating.teen': 'Tout',
  'settings.rating-title': 'Afficher {rating} dans l’arcade',
  'settings.rating-note':
    'Une machine sans classement est traitée comme si elle était classée pour les adolescents.',

  // -------------------------------------------------------------------------
  // Le bac à sable du Créateur
  // -------------------------------------------------------------------------
  'builder.sandbox-heading': 'Vous essayez le Créateur de machines',
  'builder.sandbox-lead':
    'Construisez ce que vous voulez et essayez-le ici même. Garder une machine — l’enregistrer, y jouer sur un autre appareil, la publier — fait partie de Pro.',
  'builder.sandbox-warning':
    'Votre machine est conservée dans ce navigateur et nulle part ailleurs. Effacer les données de ce navigateur, ou ouvrir le site sur un autre appareil, la perd.',
  'builder.save-needs-pro': 'Garder cette machine — Pro',

  'builder.open': 'Créer une machine',
  'builder.open-title': 'Ouvrir le Créateur de machines — concevoir et essayer ne coûte rien',

  // -------------------------------------------------------------------------
  // La coquille, les paramètres, la barrière d’erreur et la liste
  // -------------------------------------------------------------------------
  'shell.skip': 'Aller directement à la machine',
  'settings.account': 'Compte',
  'settings.account-hint':
    'Facultatif. Un compte inscrit vos scores au classement et vous permet d’ajouter des amis — la machine se joue exactement pareil sans compte.',
  'settings.account-link': 'Compte et classements',
  'settings.builder': 'Créateur de machines',
  'settings.builder-hint':
    'Concevez votre propre plateau, vos règles, vos graphismes et vos sons. Concevoir et essayer ne coûte rien ; garder une machine fait partie de Pro.',
  // Son (07 §7).
  'settings.audio': 'Son',
  'settings.audio-hint':
    'Chaque son de cette machine est généré pendant le jeu — il n’y a aucun fichier audio. Baisser une partie du son prend effet à la prochaine ouverture d’une machine.',
  'settings.audio.master': 'La machine',
  'settings.audio.master-hint':
    'Le même réglage que le bouton haut-parleur sur la machine elle-même. Le réactiver rétablit tout ce que vous avez réglé ci-dessous. Les voix des autres joueurs dans la salle ont leur propre réglage et ne sont pas touchées.',
  'settings.audio.on': 'Activé',
  'settings.audio.off': 'Coupé',
  'settings.audio-title.on': 'Laisser la machine produire du son',
  'settings.audio-title.off': 'Couper complètement le son de la machine',
  'settings.audio.volume': 'Volume général',
  'settings.audio.volume-hint':
    "Le niveau de l'ensemble, avant les quatre réglages ci-dessous. Le baisser conserve l'équilibre que vous avez choisi entre eux.",
  'settings.audio-volume-title': 'Régler le volume général sur {level}',
  'settings.audio.music': 'Musique',
  'settings.audio.music-hint':
    'La bande sonore du meuble. Elle s’efface déjà d’elle-même pendant que la machine parle.',
  'settings.audio.effects': 'Effets sonores',
  'settings.audio.effects-hint':
    'Les palettes, les champignons, les cibles, la bille qui roule sur le plateau, et les carillons du score par-dessus.',
  'settings.audio.captions': 'Sous-titres des annonces',
  'settings.audio.captions-hint':
    'Afficher par écrit ce que dit la machine. Indépendant du volume et de la coupure du son : une annonce que vous n’entendez pas reste une annonce que vous pouvez lire.',
  'settings.audio-title.captions-on': 'Afficher les sous-titres des annonces',
  'settings.audio-title.captions-off': 'Masquer les sous-titres des annonces',
  'settings.audio.speech': 'Annonces',
  'settings.audio.speech-hint':
    'La machine qui vous parle — une bille sauvée, un jackpot allumé. Les sous-titres restent affichés même sans le son.',
  'settings.audio.ambience': 'Ambiance de la salle',
  'settings.audio.ambience-hint':
    'La salle autour de la machine : les autres meubles, un peu de réverbération. Surtout audible dans un casque.',
  'settings.audio.level.full': 'Plein',
  'settings.audio.level.low': 'Plus bas',
  'settings.audio.level.off': 'Coupé',
  'settings.audio-level-title': 'Régler {group} sur {level}',
  'settings.appearance': 'Apparence',
  'settings.appearance-hint':
    'Suivre votre appareil s’ajuste quand il change — y compris au coucher du soleil, si votre téléphone le fait.',
  'settings.theme.system': 'Suivre mon appareil',
  'settings.theme.light': 'Clair',
  'settings.theme.dark': 'Sombre',
  'settings.theme-title': 'Utiliser l’apparence {theme}',
  'settings.flashing': 'Clignotement',
  'settings.flashing-hint':
    'Une machine de billard électrique clignote énormément. Réduire le clignotement conserve le sens de chaque lampe — un tir clignotant reste différent d’un tir allumé — tout en adoucissant les contrastes. Suivre votre appareil l’active lorsque vous avez demandé une réduction des animations.',
  'settings.flashing.system': 'Suivre mon appareil',
  'settings.flashing.on': 'Réduire le clignotement',
  'settings.flashing.off': 'Clignotement complet',
  'settings.flashing-title.system':
    'Réduire le clignotement lorsque votre appareil demande moins d’animations',
  'settings.flashing-title.on': 'Toujours adoucir les contrastes des lampes de la machine',
  'settings.flashing-title.off': 'Laisser la machine clignoter comme une vraie',
  'settings.height': 'Hauteur de la machine en RV',
  'settings.height-hint':
    'À quelle distance sous votre ligne de regard la machine se place. Indépendant de sa taille, et elle bouge à mesure que vous appuyez — sans casque, ce réglage ne fait rien.',
  'settings.height-title': 'Placer la machine {position}',
  'settings.height.much-lower': 'Beaucoup plus bas',
  'settings.height.lower': 'Plus bas',
  'settings.height.level': 'Au niveau',
  'settings.height.higher': 'Plus haut',
  'settings.height.much-higher': 'Beaucoup plus haut',
  'settings.onboarding': 'Pour commencer',
  'settings.onboarding-hint':
    'Jouer une bille, monter une rampe, lancer un mode, garder votre pointage. Elles disparaissent pour de bon une fois les quatre faites.',
  'settings.motion': 'Mouvement de la caméra',
  'settings.motion-hint':
    'À quel point la vue tremble et s’incline sur un gros tir. Si votre appareil demande moins d’animations, la machine reste immobile quel que soit le choix fait ici.',
  'settings.motion.full': 'Complet',
  'settings.motion.half': 'Moitié',
  'settings.motion.hint': 'Un soupçon',
  'settings.motion.none': 'Aucun',
  'settings.motion-title': 'Régler le mouvement de la caméra à « {amount} »',
  'settings.graphics': 'Graphismes',
  'settings.graphics-hint':
    'Le mode automatique mesure cet appareil et choisit — {describe}. Forcer un niveau empêche la machine de se rabaisser d’elle-même quand les images tardent.',
  'settings.graphics.automatic': 'Automatique',
  'settings.graphics-automatic-title': 'Mesurer cet appareil et choisir automatiquement',
  'settings.graphics-tier-title': 'Toujours afficher au niveau {tier}',
  'settings.graphics-note':
    'Un changement prend effet à la prochaine ouverture d’une machine : reconstruire un niveau en pleine bille détruirait la scène sous une bille en jeu.',
  'settings.nudging': 'Coups de hanche',
  'settings.nudging-hint':
    'Un coup de pouce sur le plateau donne toujours un coup de hanche à la machine. Bouger le téléphone lui-même est le supplément, et cela demande la permission de votre navigateur.',
  'settings.nudging.both': 'Coup de pouce et mouvement du téléphone',
  'settings.nudging.flick': 'Coup de pouce seulement',
  'settings.nudging-title.both':
    'Donner un coup de hanche en touchant le plateau et en bougeant le téléphone',
  'settings.nudging-title.flick': 'Donner un coup de hanche en touchant le plateau seulement',
  'settings.nudging-refused':
    'Votre navigateur a refusé le capteur de mouvement. On peut habituellement le réautoriser dans les paramètres du site pour cette page; le coup de pouce sur le plateau fonctionne dans tous les cas.',
  'settings.nudging-note': 'Prend effet à la prochaine ouverture d’une machine.',
  'settings.touch': 'Commandes tactiles',
  'settings.touch-hint-1':
    'Maintenez le côté gauche ou droit de la bande sous le plateau pour ce palet — les deux à la fois fonctionnent. Tirez',
  'settings.touch-hint-down': 'vers le bas',
  'settings.touch-hint-2':
    'depuis le coin inférieur droit pour tirer le lanceur; plus vous tirez loin, plus la bille part fort. Touchez le plateau pour donner un coup de hanche.',
  'settings.touch.show-again': 'Afficher de nouveau les zones',
  'settings.touch.will-show': 'Les zones seront affichées',
  'settings.touch-title': 'Afficher de nouveau les zones des palets à la prochaine bille',
  'settings.touch-note':
    'La bordure brille jusqu’à la première pression sur un palet, puis s’estompe pour que la machine ne soit pas couverte de boutons.',
  'settings.haptics': 'Vibration',
  'settings.haptics-hint':
    'Un clic quand un palet part, un choc quand la bille frappe quelque chose de dur, et une double pulsation quand elle est perdue. Rien d’autre ne fait vibrer le téléphone.',
  'settings.haptics.on': 'Activée',
  'settings.haptics.off': 'Désactivée',
  'settings.haptics-title.on': 'Sentir la machine à travers le téléphone',
  'settings.haptics-title.off': 'Jouer sans vibration',
  'settings.headsets': 'Casques',
  'settings.headsets-hint':
    'NEON NOVA se joue en RV et en réalité mixte sur un Quest. Ouvrir cette page dans le casque, ou balayer le code, y transporte la même machine.',
  'settings.headsets-link': 'Jouer dans un casque',
  'settings.kept': 'Conservé sur cet appareil',
  'settings.kept-looking': 'Recherche en cours…',
  'settings.kept-summary-one':
    '1 score et {replays} des {max} dernières parties, plus une partie inachevée si vous en avez laissé une. Aucun compte, et rien n’est envoyé nulle part.',
  'settings.kept-summary-many':
    '{scores} scores et {replays} des {max} dernières parties, plus une partie inachevée si vous en avez laissé une. Aucun compte, et rien n’est envoyé nulle part.',
  'settings.kept-unavailable':
    'Ce navigateur ne laisse rien conserver à la machine, donc rien n’est stocké. Les parties se jouent normalement; elles sont simplement oubliées.',
  'settings.kept-forget': 'Tout oublier',
  'settings.kept-forget-title':
    'Supprimer les scores, les reprises et la partie inachevée conservés sur cet appareil',
  'boundary.heading': 'Cela ne s’est pas passé comme prévu',
  'boundary.body':
    'Quelque chose dans la page a cessé de fonctionner. Vous n’y êtes pour rien, et rien de ce que vous avez joué n’est perdu — les scores sur cet appareil sont enregistrés à la fin de chaque partie.',
  'boundary.what-next': 'Que faire ensuite',
  'boundary.reload': 'Recharger',
  'boundary.reload-title': 'Recharger la page',
  'boundary.back': 'Retour à la machine',
  'boundary.back-title': 'Retourner à la machine',
  'arcade.lede':
    'Chaque machine ici est générée en code — aucune photographie, aucun échantillon, aucun modèle. Jouez-y sans compte; gardez le score quand vous voulez le garder.',
  'notfound.play-title': 'Jouer à NEON NOVA',

  // -------------------------------------------------------------------------
  // La zone du compte
  // -------------------------------------------------------------------------
  'account.no-backend':
    'Cette version n’a aucun serveur configuré, donc les comptes ne sont pas disponibles. Tout le reste — la machine, vos scores sur cet appareil, les paramètres — fonctionne exactement comme avec un serveur.',
  'account.checking': 'Vérification…',
  'account.lede':
    'Un compte inscrit vos scores au classement, vous permet d’ajouter des amis et conserve vos parties quand vous changez d’appareil.',
  'account.no-need': 'Vous n’en avez pas besoin pour jouer',
  'account.what-do': 'Que souhaitez-vous faire',
  'account.mode.sign-in': 'Se connecter',
  'account.mode.register': 'Créer un compte',
  'account.mode.reset': 'Envoyer un lien de réinitialisation',
  'account.email-title': 'Votre adresse courriel',
  'account.password-title': 'Votre mot de passe',
  'account.sign-out': 'Se déconnecter',
  'account.sign-out-title': 'Se déconnecter de ce compte',
  'account.new-password-heading': 'Choisissez un nouveau mot de passe',
  'account.new-password': 'Nouveau mot de passe',
  'account.new-password-title': 'Votre nouveau mot de passe',
  'account.set-password-title': 'Définir ce mot de passe',
  'account.handle-lede':
    'Une dernière chose : un pseudonyme. Il apparaît à côté de vos scores, donc il est public et il est à vous — de 3 à 24 caractères, minuscules, chiffres et traits d’union.',
  'account.handle-title': 'Votre pseudonyme public',
  'account.display-name': 'Nom affiché (facultatif)',
  'account.display-name-title': 'Le nom affiché à côté de votre pseudonyme',
  'account.claim-title': 'Réserver ce pseudonyme',
  'delete.heading': 'Supprimer ce compte',
  'delete.lede':
    'Supprime votre profil et tout ce qui y est rattaché de HCC Pinball Arcade. Votre identifiant de connexion n’est pas supprimé — le même courriel continue de fonctionner pour les autres applications qui l’utilisent.',
  'delete.open': 'Supprimer le compte…',
  'delete.open-title': 'Voir ce que la suppression de ce compte enlèverait',
  'delete.has-subscription': 'Vous avez un abonnement actif.',
  'delete.not-cancelled': 'Supprimer votre profil ne l’annule pas.',
  'delete.removes': 'Ceci supprime, de façon permanente :',
  'delete.keeps': 'Ceci ne touche pas à :',
  'delete.confirm-title': 'Votre pseudonyme, saisi exactement, pour confirmer la suppression',
  'delete.keep': 'Garder mon compte',
  'delete.keep-title': 'Garder ce compte',
  'delete.confirm-button-title': 'Supprimer ce compte et tout ce qui est listé ci-dessus',
  'friends.add': 'Ajouter par pseudonyme',
  'friends.add-title': 'Le pseudonyme du joueur à ajouter',
  'friends.send-title': 'Envoyer une demande d’ami',
  'friends.waiting-you': 'En attente de votre réponse',
  'friends.waiting-them': 'En attente de leur réponse',
  'friends.accept': 'Accepter',
  'friends.decline': 'Refuser',
  'friends.block': 'Bloquer',
  'friends.empty':
    'Personne pour l’instant. Ajoutez quelqu’un par son pseudonyme et ses scores apparaîtront sur votre tableau.',
  'ghost.needs-account': 'Les reprises nécessitent un compte.',
  'ghost.loading': 'Chargement…',
  'ghost.back': 'Retour aux classements',
  'ghost.playback': 'Lecture de la reprise',
  'ghost.scrub-title': 'Parcourir la reprise',
  'ghost.speed': 'Vitesse de lecture',
  'ghost.pause': 'Pause',
  'ghost.pause-title': 'Mettre la lecture en pause',
  'ghost.camera': 'Caméra',
  'boards.heading': 'Classements',
  'boards.no-backend':
    'Cette version n’a aucun serveur configuré, donc il n’y a aucun classement à afficher.',
  'boards.sign-in': 'Connectez-vous ou créez-en un',
  'boards.input': 'Commande',
  'boards.input.any': 'Toute commande',
  'boards.input.keyboard': 'Clavier',
  'boards.input.touch': 'Tactile',
  'boards.input.gamepad': 'Manette',
  'boards.input.motion': 'Mouvement',
  'boards.input.xr-hands': 'Mains en RV',
  'boards.input.xr-controllers': 'Manettes en RV',
  'boards.input.cabinet': 'Caisse',
  'boards.machine-picker': 'Quelle machine',
  'boards.machine-title': 'Afficher le classement de {title}',
  'boards.filters': 'Affichage : {period}, {scope}, {input}.',
  'boards.period': 'Période',
  'boards.scope': 'Qui figure dans ce classement',
  'boards.more-title': 'Afficher plus de ce classement',
  'machines.mine.heading': 'Vos machines',
  'machines.mine.looking': 'Recherche de vos machines…',
  'machines.mine.problem': 'Vos machines n’ont pas pu être lues pour l’instant. Rien n’est perdu.',
  'machines.mine.none': 'Vous n’avez pas encore créé de machine. Concevoir et essayer ne coûte rien.',
  'machines.mine.open': 'Ouvrir',
  'machines.mine.open-title': 'Ouvrir {title} dans le Créateur de machines',
  'machines.mine.no-version': 'Rien d’enregistré pour l’instant',
  'machines.mine.edited': 'dernier enregistrement {when}',
  'machines.mine.status.draft': 'Brouillon',
  'machines.mine.status.review': 'En révision',
  'machines.mine.status.published': 'Publiée',
  'machines.mine.status.unpublished': 'Dépubliée',
  'headsets.heading': 'Appareils Quest liés',
  'headsets.manage': 'Gérer les appareils Quest',
  'headsets.link': 'Lier un autre Quest',
  'headsets.refresh': 'Actualiser les appareils',
  'headsets.multiple':
    'Vous pouvez lier plusieurs Quest à ce compte. Saisissez le code propre à chaque casque. Vos appareils existants restent liés.',
  'headsets.empty': 'Aucun appareil Quest lié. Choisissez Lier un autre Quest et saisissez le code affiché dans le jeu sur votre casque.',
  'headsets.reference': 'ID de liaison {reference}',
  'headsets.looking': 'Recherche de casques jumelés…',
  'headsets.keep': 'Le garder',
  'headsets.remove': 'Délier',
  'migration.empty':
    'Rien à transférer depuis ce navigateur pour l’instant. Jouez une partie et elle sera proposée ici.',
  'migration.heading': 'Parties sur ce navigateur',
  'migration.send-title': 'Envoyer ces parties pour vérification',
  'pair.heading': 'Jumeler un casque',
  'pair.no-backend':
    'Cette version n’a aucun serveur configuré, donc il n’y a rien à quoi jumeler un casque.',
  'pair.go-account': 'Aller à votre compte',
  'pair.put-on': 'Mettez le casque, choisissez',
  'pair.code': 'Code de jumelage',
  'pair.code-title': 'Les huit caractères affichés sur le casque',
  'pair.lookup-title': 'Rechercher ce code de jumelage',
  'pair.is-yours': 'Est-ce bien votre casque?',
  'pair.meta-account': 'Compte Meta qu’il déclare être',
  'pair.meta-id': 'Fin de l’identifiant Meta',
  'pair.confirm-title': 'Confirmez que vous avez vous-même lancé ce jumelage sur un casque',
  'pair.approve-off':
    'L’approbation reste inactive tant que vous n’avez pas confirmé la ligne ci-dessus.',
  'pair.approve-title': 'Connecter ce casque en votre nom',
  'pair.refuse-title': 'Refuser ce jumelage',
  'pair.see-it-on': 'Une fois la connexion du casque terminée, vous pouvez le voir et le délier dans',
  'pair.again': 'Lier un autre Quest',
  'pair.again-title': 'Saisir le code d’un autre Quest',
  'pair.refused':
    'Refusé. Ce casque a reçu un non et le code est maintenant mort — plus rien ne peut être fait avec.',
  'pair.refused-safe':
    'S’il ne s’agissait pas d’un de vos casques, personne n’est entré : un code refusé ne peut pas être échangé, et il n’a jamais été une session de toute façon.',

  // -------------------------------------------------------------------------
  // Actions sur les demandes d’ami
  // -------------------------------------------------------------------------
  'friends.accept-title': 'Accepter la demande de {handle}',
  'friends.decline-title': 'Refuser la demande de {handle}',
  'friends.block-title': 'Bloquer {handle}',
  'friends.this-player': 'ce joueur',

  // -------------------------------------------------------------------------
  // États et replis que la première passe avait manqués
  // -------------------------------------------------------------------------
  'common.working': 'Traitement…',
  'common.unknown-handle': 'inconnu',
  'friends.heading': 'Amis',
  'friends.sending': 'Envoi…',
  'friends.send': 'Envoyer une demande',
  'account.bad-email': 'Cela ne ressemble pas à une adresse courriel.',
  'account.set-password': 'Définir ce mot de passe',
  'account.claiming': 'Réservation…',
  'account.claim': 'Réserver ce pseudonyme',
  'delete.deleting': 'Suppression…',
  'delete.confirm': 'Supprimer définitivement',
  'ghost.outside-db':
    'Cette reprise est stockée hors de la base de données et ne peut pas encore être visionnée.',
  'ghost.machine-gone':
    'La machine sur laquelle cette partie a été jouée n’est plus disponible.',
  'boards.period.all-time': 'Depuis toujours',
  'boards.period.month': 'Ce mois-ci',
  'boards.period.week': 'Cette semaine',
  'boards.scope.global': 'Tout le monde',
  'boards.scope.friends': 'Amis',
  'boards.empty-friends': 'Personne que vous connaissez n’a encore de score ici.',
  'boards.empty-global': 'Aucun score pour l’instant. Le premier pourrait être le vôtre.',
  'boards.machine': 'Machine',
  'boards.loading': 'Chargement…',
  'boards.more': 'Afficher plus',
  'headsets.removing': 'Déliaison…',
  'headsets.remove-confirm': 'Oui, le délier',
  'migration.none-sent': 'Aucune de ces parties n’a pu être envoyée.',
  'migration.one-game': 'Une partie',
  'migration.one-refused': 'Une partie n’a pas pu être acceptée.',
  'migration.one-queued':
    'Une est encore en file d’attente et sera envoyée dès que la connexion le permettra.',
  'pair.checking-signed-in': 'Vérification de votre connexion…',
  'pair.checking': 'Vérification…',
  'pair.continue': 'Continuer',
  'pair.approving': 'Approbation…',
  'pair.approve': 'Approuver',
  'pair.refusing': 'Refus…',
  'pair.type-again': 'Saisir de nouveau un code de jumelage',
  'pair.try-another': 'Essayer un autre code',
  'pair.start-again': 'Recommencer',

  // -------------------------------------------------------------------------
  // Intitulés des champs du compte
  // -------------------------------------------------------------------------
  'account.heading': 'Compte',
  'account.email': 'Courriel',
  'account.password': 'Mot de passe',
  'account.handle': 'Pseudonyme',
  'account.name': 'Nom',
  'account.plan': 'Forfait',
  'account.plan.free': 'Gratuit',
  'account.plan.pro-forever': 'Pro — sans date de fin',
  'account.plan.pro-until': 'Pro — jusqu’au {date}',
  'account.change': 'Modifier',
  'account.change-handle-title': 'Modifier votre pseudonyme public',
  'account.change-name-title': 'Modifier le nom affiché à côté de votre pseudonyme',
  'account.new-handle-title': 'Votre nouveau pseudonyme',
  'account.new-name-title': 'Votre nouveau nom affiché',
  'account.save': 'Enregistrer',
  'account.save-handle-title': 'Enregistrer le nouveau pseudonyme',
  'account.save-name-title': 'Enregistrer le nouveau nom',
  'account.saving': 'Enregistrement…',
  'account.cancel': 'Annuler',
  'account.cancel-title': 'Garder tel quel',
  'account.saved': 'Enregistré.',
  'account.handle-rule':
    'Votre pseudonyme est la façon dont les autres joueurs vous trouvent. Vous pouvez le changer une fois tous les 30 jours, et votre ancien pseudonyme vous est réservé pendant 30 jours.',
  'account.handle-next': 'Vous pourrez changer de pseudonyme le {date}.',

  // -------------------------------------------------------------------------
  // Fin de l’introduction du compte
  // -------------------------------------------------------------------------
  'account.lede-tail': 'la machine fonctionne exactement pareil dans les deux cas.',

  // -------------------------------------------------------------------------
  // Retrait de casque et transfert de parties
  // -------------------------------------------------------------------------
  'headsets.remove-question': 'Délier {label}?',
  'headsets.remove-title': 'Délier {label} de votre compte',
  'headsets.keep-title': 'Garder {label} jumelé',
  'migration.sending': 'Envoi de {done} sur {total}…',
  'migration.sent':
    '{count} envoyée(s) pour vérification. Un score apparaît au classement une fois que le serveur l’a rejouée.',
  'migration.n-games': '{count} parties',
  'migration.lede-tail':
    'jouée(s) avant votre connexion. Les envoyer les met dans la file d’attente de vérification — le serveur rejoue chacune d’elles et ce n’est qu’ensuite qu’elle compte, exactement comme pour une partie jouée maintenant.',
  'migration.n-refused': '{count} parties n’ont pas pu être acceptées.',
  'migration.n-queued':
    '{count} sont encore en file d’attente et seront envoyées dès que la connexion le permettra.',

  // -------------------------------------------------------------------------
  // Conséquences de la suppression du compte
  // -------------------------------------------------------------------------
  'delete.consequence.0':
    'Votre pseudonyme, votre nom affiché et votre avatar. Le pseudonyme redevient disponible pour quelqu’un d’autre.',
  'delete.consequence.1':
    'Chaque score que vous avez réalisé, dans chaque classement, y compris les inscriptions aux tournois.',
  'delete.consequence.2':
    'Chaque reprise que vous avez soumise, y compris celles qui servent de preuve derrière une entrée au classement.',
  'delete.consequence.3':
    'Chaque machine que vous avez construite — brouillons comme versions publiées.',
  'delete.consequence.4':
    'Les scores que d’autres joueurs ont réalisés sur les machines que vous avez publiées, puisque les machines partent avec vous.',
  'delete.consequence.5': 'Votre liste d’amis, des deux côtés.',
  'delete.consequence.6': 'Tout casque jumelé à ce compte.',
  'delete.consequence.7':
    'Votre dossier d’abonnement ici. **Ceci n’annule pas l’abonnement lui-même.**',
  'delete.keeps.0':
    'Votre identifiant de connexion lui-même. Le même courriel permet toujours de se connecter aux autres applications de ce compte.',
  'delete.keeps.1':
    'Votre abonnement Stripe ou Meta. **Annulez-le d’abord, sinon vous continuerez d’être facturé.**',
  'delete.paying-tail':
    'Annulez-le d’abord, sinon vous continuerez d’être facturé pour un compte qui n’existe plus.',
  'delete.type-handle': 'Saisissez votre pseudonyme — {handle} — pour confirmer',

  // -------------------------------------------------------------------------
  // Tableau des classements
  // -------------------------------------------------------------------------
  'boards.needs-account':
    'Les classements nécessitent un compte, parce qu’un score y porte un nom à côté.',
  'boards.keep-playing': '— vous pouvez continuer à jouer dans tous les cas.',
  'boards.caption': 'Classement de {machine}, {period}, {scope}',
  'boards.rank': 'Rang',
  'boards.player': 'Joueur',
  'boards.score': 'Score',
  'boards.watch': 'Regarder',

  // -------------------------------------------------------------------------
  // Visionneuse de reprises
  // -------------------------------------------------------------------------
  'ghost.heading': 'Regarder',
  'ghost.signin': 'Se connecter',
  'ghost.keep-playing': '— vous pouvez continuer à jouer dans tous les cas.',
  'ghost.checked': '{score} — vérifié par le serveur',
  'ghost.unchecked': '{score} — {status} (état)',
  'ghost.position': 'Position {at} sur {total}',
  'ghost.speed-title': 'Lire à {speed} fois la vitesse',
  'ghost.camera-title': 'Regarder depuis la caméra {preset}',

  // -------------------------------------------------------------------------
  // Détails du jumelage de casque
  // -------------------------------------------------------------------------
  'pair.sign-in-first':
    'Connectez-vous d’abord — jumeler un casque le connecte en votre nom, il faut donc que ce soit vous qui le demandiez.',
  'pair.put-on-tail':
    'et saisissez ici les huit caractères qu’il affiche. Le code est valide dix minutes.',
  'pair.sign-in-word': 'Se connecter',
  'pair.code-hint':
    'Lettres et chiffres seulement — le code ne contient jamais O, I, L, U, zéro ni un, alors ces touches ne font rien ici. {entered} sur 8.',
  'pair.device': 'Appareil',
  'pair.as-reported': '(tel que déclaré par le casque)',
  'pair.started-myself': 'J’ai lancé ce jumelage moi-même, sur un casque que je tiens.',
  'pair.not-mine': 'Ce n’est pas le mien',
  'pair.approved':
    'Approuvé. {device} se connecte maintenant — cela peut prendre quelques secondes.',
  'pair.your-account': 'votre compte',

  // -------------------------------------------------------------------------
  // État d’une reprise, en mots plutôt qu’en énumération
  // -------------------------------------------------------------------------
  'ghost.status.pending': 'en attente de vérification',
  'ghost.status.validated': 'vérifié par le serveur',
  'ghost.status.mismatch': 'le score ne correspondait pas à la reprise',
  'ghost.status.divergent': 'la reprise ne s’est pas rejouée de la même façon',
  'ghost.status.unsupported': 'jouée sur une version que le serveur ne peut pas rejouer',
  'ghost.status.oversize': 'trop volumineuse pour être vérifiée',

  // -------------------------------------------------------------------------
  // La scène, le passage au casque et l’habillage
  // -------------------------------------------------------------------------
  'stage.building': 'Construction de la machine',
  'stage.status': 'État de la machine',
  // Le menu de débordement de l'écran plat (M3-55).
  'stage.menu-open': 'Plus',
  'stage.menu-close': 'Fermer le menu',
  'stage.menu-open-title': 'Ouvrir le menu de la machine',
  'stage.menu-close-title': 'Fermer ce menu',
  'stage.menu-label': 'Menu de la machine',
  'stage.menu-confirm': 'Vraiment {action} ?',
  'stage.menu-confirm-title': 'Appuyez de nouveau pour {action}. C’est irréversible.',
  'stage.menu.pause': 'Pause',
  'stage.menu.resume': 'Reprendre',
  'stage.menu.pause-title': 'Arrêter la machine où elle en est',
  'stage.menu.resume-title': 'Relancer la machine',
  'stage.menu.settings': 'Paramètres',
  'stage.menu.settings-title': 'Son, apparence et confort',
  'stage.menu.another-machine': 'Salle d’arcade',
  'stage.menu.another-machine-title': 'Parcourir les flippers et choisir une machine',
  'stage.menu.end-game': 'Terminer la partie',
  'stage.menu.end-game-title': 'Abandonner cette partie et oublier votre progression',
  'stage.invite-gone':
    'Cette partie est déjà terminée. Vous jouez seul — demandez-leur une autre invitation.',
  'stage.aloud': 'Lire la machine à voix haute',
  'stage.aloud-title':
    'Dire ce que fait la machine — bille, pointage, mode, ce qui est allumé, la suite',
  'stage.sound-on': 'Son activé',
  'stage.sound-off': 'Son coupé',
  'stage.sound-off-title': 'Couper le son de la machine',
  'stage.sound-on-title': 'Réactiver le son de la machine',
  'stage.pause': 'En cours',
  'stage.pause-title': 'Mettre la machine en pause',
  'stage.resume-machine': 'En pause',
  'stage.resume-machine-title': 'Laisser la machine reprendre',
  'stage.put-ball': 'Mettre une bille en jeu',
  'stage.motion-offer': 'Donner un coup de hanche en bougeant votre téléphone',
  'stage.motion-on-title': 'Bouger le téléphone pour donner un coup de hanche à la machine',
  'stage.motion-off-title': 'Continuer à donner des coups de hanche en touchant le plateau',
  'stage.turn-on': 'L’activer',
  'stage.no-thanks': 'Non merci',
  'stage.motion-enabled':
    'Le coup de hanche par mouvement est activé — secouez le téléphone pour l’utiliser.',
  'stage.motion-refused':
    'Votre navigateur n’a pas autorisé le coup de hanche par mouvement — touchez le plateau pour l’utiliser.',
  'stage.dismiss': 'Fermer',
  'stage.dismiss-title': 'Fermer cet avis',
  'stage.focus-on': 'Mode plein écran',
  'stage.focus-off': 'Quitter le mode plein écran',
  'stage.focus-on-title': 'Donner tout l’écran à la machine',
  'stage.focus-off-title': 'Afficher de nouveau les barres',
  'stage.players': 'Joueurs',
  'stage.players-title': 'Combien de joueurs',
  'stage.play-alone': 'Jouer seul',
  'stage.play-together': 'Jouer ensemble',
  'stage.opening-game': 'Ouverture d’une partie…',
  'stage.open-game-title': 'Ouvrir sur cette machine une partie que d’autres peuvent rejoindre',
  'stage.online-game': 'Partie en ligne',
  'stage.start': 'Commencer',
  'stage.start-title': 'Commencer la partie pour tous les joueurs assis',
  'stage.leave': 'Quitter',
  'stage.leave-title': 'Quitter cette partie et jouer seul',
  'stage.game-ended': 'Partie terminée. La machine est prête pour une nouvelle.',
  'stage.your-ball': 'Votre bille.',
  'stage.game-over-note': 'Cette partie est terminée.',
  'stage.game-called-off': 'Cette partie a été annulée.',
  'stage.somebody': 'Quelqu’un',
  'stage.somebody-else': 'quelqu’un d’autre',
  'stage.resume': 'Reprendre',
  'stage.resume-heading': 'Reprendre votre partie',
  'stage.resume-title': 'Continuer la partie que vous avez laissée',
  'stage.start-fresh': 'Recommencer',
  'stage.start-fresh-title': 'L’oublier et commencer une nouvelle partie',
  'stage.initials-title': 'Saisir vos initiales',
  'stage.initials-help':
    'Les palets changent la lettre et le lanceur la valide, exactement comme sur la borne. Choisissez < pour revenir d’une lettre.',
  'stage.previous-letter': 'Lettre précédente',
  'stage.accept-letter': 'Valider cette lettre',
  'stage.next-letter': 'Lettre suivante',
  'stage.ok': 'OK',
  'stage.game-over': 'Partie terminée',
  'stage.play-again-title': 'Commencer une autre partie',
  'stage.best-yet': 'Votre meilleur score sur cet appareil',
  'stage.kept-here': 'Conservé sur cet appareil.',
  'stage.cannot-keep': 'Ce navigateur ne laisse rien conserver à la machine.',
  'stage.real-score':
    'C’est un vrai score. Le conserver, le revoir et l’inscrire au classement nécessitent tous un compte.',
  'stage.make-account': 'Créer un compte gratuit',
  'stage.boards-coming': 'Les comptes arrivent avec les classements',
  'stage.keep-score': 'Conserver ce score',
  'stage.keep-score-title':
    'Créez un compte gratuit — les scores, les rediffusions et les classements ne coûtent rien',
  'stage.pro-adds':
    'Toutes les autres machines de l’arcade, et celles créées par d’autres joueurs, font partie de Pro.',
  'stage.more-machines': 'Voir les autres machines',
  'stage.more-machines-title': 'Ce que Pro ajoute au compte que vous avez déjà',
  'stage.build-line':
    'Le Créateur de machines est compris dans votre abonnement. Votre propre machine part de celle-ci.',
  'stage.build-aside':
    'Une machine bien à vous commence dans le Créateur, qui est ouvert à tout le monde.',
  'stage.pull-down': 'TIREZ',
  // --- quelle machine la porte d’entrée fait tourner (M9-32) -------------------------------
  'stage.playing': 'En jeu',
  'stage.switch-to-draft': 'Jouer votre machine',
  'stage.switch-to-draft-title':
    'Jouer la machine que vous construisez, en plein écran et sans score',
  'stage.your-draft': 'votre machine',
  'stage.switch-to-flagship': 'Jouer NEON NOVA',
  'stage.switch-to-flagship-title':
    'Revenir à la machine par défaut. Votre machine est conservée',
  'stage.unscored': 'Un brouillon — rien n’est compté ici',
  'stage.unscored-title':
    'Une machine ne peut être classée qu’une fois publiée, car un classement resimule les octets exacts sur lesquels une partie a été jouée',
  'xr.exit': 'Quitter la RX',
  'xr.exit-title': 'Quitter le casque et revenir à la machine en fenêtre',
  'xr.enter-vr': 'Entrer en RV',
  'xr.enter-mr': 'Entrer en RM',
  'xr.build-vr': 'Construire en RV',
  'xr.build-mr': 'Construire en RM',
  'xr.build-title': 'Votre machine sur l’établi, à mains nues — ce pour quoi vous êtes venu',
  'xr.vr-title': 'Jouer dans l’arcade en RV',
  'xr.mr-title': 'Installer la machine dans votre propre pièce',
  'xr.handoff-title': 'Afficher un code pour ouvrir ceci sur votre casque',
  'xr.no-session':
    'Le casque n’a pas voulu démarrer une session. Réessayez, ou vérifiez ses permissions.',
  'headset.lede':
    'La RV et la réalité mixte fonctionnent dans le navigateur du casque. Ouvrez cette page là-bas et la machine reprend exactement où vous l’avez laissée.',
  'headset.scan': 'Balayez avec la caméra du casque, ou saisissez l’adresse ci-dessous.',
  'headset.qr-title': 'Code QR pour {url}',
  'headset.step-1': 'Mettez le casque et ouvrez son navigateur.',
  'headset.step-2': 'Balayez le code ci-dessus, ou saisissez l’adresse.',
  'headset.step-3': 'Connectez-vous si on vous le demande — c’est le même compte.',
  'headset.step-4':
    '« Entrer en RV » ou « Entrer en RM » se trouve sur la machine elle-même.',
  'headset.no-headset': 'Pas encore de casque?',
  'headset.windowed': 'La machine en fenêtre',
  'voice.mic-on': 'Activer votre microphone',
  'voice.mic-off': 'Désactiver votre microphone',
  'voice.blocked': 'Votre navigateur n’autorise pas cette page à utiliser un microphone',
  'voice.chat-on': 'Activer la conversation vocale',
  'voice.chat-off': 'Désactiver la conversation vocale',
  'nav.back': 'Retour',
  'onboarding.title': 'Pour commencer',
  'onboarding.progress': '{done} sur {total} de fait',
  'onboarding.play-ball': 'Jouer une bille',
  'onboarding.make-ramp': 'Monter une rampe',
  'onboarding.start-mode': 'Lancer un mode',
  'onboarding.save-score': 'Garder votre pointage',
  'onboarding.done': 'fait',
  'onboarding.next': 'à suivre',
  'onboarding.save-cta': 'Garder vos pointages',
  'onboarding.save-title': 'Créer un compte pour que la machine se souvienne de vous',
  'onboarding.forget': 'Revoir les étapes de départ',
  'onboarding.forget-title': 'Recommencer les quatre étapes',
  'nav.home-title': 'HCC Pinball Arcade — accueil',
  'nav.primary': 'Principale',
  'nav.scores': 'Scores',
  'theme.toggle-title': '{description}. Touchez pour changer.',
  'play.not-found-heading': 'Aucune machine nommée « {machineId} »',
  'play.not-found-body':
    'Elle a peut-être été retirée, ou le lien est mal saisi. L’arcade contient ce qui existe.',
  'play.go-arcade': 'Aller à l’arcade',
  'play.browse-title': 'Parcourir les machines',
  // Les trois refus de G1-07, et les deux états d’un chargement qui mène à l’un d’eux.
  'play.loading': 'Préparation de {machineId}…',
  'play.loading-title': 'La machine est en cours de téléchargement',
  'play.locked-heading': '« {machineId} » fait partie de Pro',
  'play.locked-body':
    'Toutes les machines sauf la vedette viennent avec un abonnement. NEON NOVA reste gratuite, tout comme la machine gratuite de la semaine.',
  'play.locked-cta': 'Voir ce que comprend Pro',
  'play.locked-title': 'Ouvrir la page d’abonnement',
  'play.play-flagship': 'Jouer plutôt à NEON NOVA',
  'play.unreadable-heading': '« {machineId} » ne fonctionne pas dans cette version',
  'play.unreadable-body':
    'La machine a été téléchargée et cette version l’a refusée. C’est une faute de la machine ou de l’application, jamais de votre lien — rien là-dedans ne vient de vous.',
  'play.offline-heading': 'Impossible de joindre l’arcade',
  'play.offline-body':
    'La liste des machines exige une connexion. NEON NOVA se joue sans, et tout ce qui est déjà téléchargé s’ouvre encore.',
  'play.retry': 'Réessayer',
  'play.retry-title': 'Redemander la machine',

  // -------------------------------------------------------------------------
  // Le tableau des scores à côté de la vitre
  // -------------------------------------------------------------------------
  'scoreboard.player': 'Joueur {seat}',
  'scoreboard.tilted': 'basculé',
  'scoreboard.left': 'parti',
  'scoreboard.playing': 'en train de jouer',

  // -------------------------------------------------------------------------
  // Détails du passage au casque
  // -------------------------------------------------------------------------
  'headset.no-code': 'Cette adresse ne peut pas être convertie en code balayable',
  'headset.no-code-tail': '. Saisissez-la à la main.',
  'headset.press': 'Appuyez sur',
  'headset.or': 'ou',
  'headset.on-machine': 'sur la machine.',
  'headset.windowed-tail': 'propose le même jeu, avec la même physique et les mêmes classements.',

  // -------------------------------------------------------------------------
  // Places, tour de jeu et observation
  // -------------------------------------------------------------------------
  'stage.take-turns': 'Jouer à tour de rôle avec {others} autres sur cet appareil',
  'stage.ball-of': 'Bille {ball} sur {total}',
  'stage.is-playing': '{name} est en train de jouer.',
  'stage.watching-gapped': 'Vous regardez la bille de {name} — rattrapage en cours.',
  'stage.watching-diverged':
    'Vous regardez la bille de {name} — l’image n’est plus synchronisée avec la partie en direct.',
  'stage.watching-live': 'Vous regardez la bille de {name}, en direct.',
  'stage.their-ball': 'C’est la bille de {name}',

  // -------------------------------------------------------------------------
  // Reprise, initiales et fin de partie
  // -------------------------------------------------------------------------
  'stage.left-game': 'Vous avez laissé une partie à {score}.',
  'stage.left-game-ball': 'Vous avez laissé une partie à la bille {ball}, à {score}.',
  'stage.makes-table': '{score} entre au tableau. Inscrivez-y vos initiales.',
  'stage.skip-initials': 'Passer',
  'stage.skip-initials-title': 'Garder le score sans y mettre de nom',
  'stage.play-again': '▸ REJOUER',
  'stage.until-then': '— d’ici là, cette machine ne demande rien.',

  // -------------------------------------------------------------------------
  // L’espace de travail du Créateur
  // -------------------------------------------------------------------------
  'builder.tab.layout': 'Plateau',
  'builder.tab.rules': 'Règles',
  'builder.tab.modes': 'Modes',
  'builder.tab.art': 'Illustration',
  'builder.tab.display': 'Afficheur',
  'builder.tab.sound': 'Son',
  'builder.tab.speech': 'Voix',
  'builder.tab.checks': 'Vérifications',
  'builder.opening': 'Ouverture de {machineId}…',
  'builder.locked-heading': '« {machineId} » fait partie de Pro',
  'builder.locked-body':
    'Le concepteur lui-même est ouvert à tous — concevez, essayez, prenez tout votre temps. Ce que l\u2019abonnement achète, c\u2019est qu\u2019une machine survive au rechargement, et la lecture d\u2019une machine publiée par quelqu\u2019un d\u2019autre.',
  'builder.not-found-body':
    'Rien n\u2019est publié sous ce nom. La machine a peut-être été retirée, ou le lien est mal saisi.',
  'builder.offline-body':
    'Ouvrir une machine publiée exige une connexion. Un espace de travail vierge, non : il s\u2019ouvre sur le corps de la machine vedette.',
  'builder.open-blank': 'Partir du corps de la machine vedette',
  'builder.open-blank-title': 'Ouvrir le concepteur sur NEON NOVA',
  'builder.workspaces': 'Espaces de travail',
  'builder.tab.layout-title': 'Le plateau : où se trouve chaque chose',
  'builder.tab.rules-title': 'Les règles : ce que fait la machine quand quelque chose se produit',
  'builder.tab.modes-title': 'Les modes : les ensembles de tirs que le joueur poursuit',
  'builder.tab.art-title': 'L’illustration : l’apparence de la machine, dessinée en code',
  'builder.tab.display-title': 'L’afficheur : ce que la matrice de points montre, et quand',
  'builder.tab.sound-title':
    'Le son : quelle recette joue chaque signal, et comment elle est réglée',
  'builder.tab.speech-title':
    'Les annonces : ce que dit la machine, et le sous-titre que lit le joueur',
  'builder.tab.checks-title':
    'Les vérifications : les seize classes de validation, sur la machine telle quelle',
  'builder.editing': 'Édition',
  'builder.canvas-controls': 'Commandes du canevas',
  'builder.fit': 'Ajuster',
  'builder.fit-title': 'Afficher tout le plateau',
  'builder.true-scale': 'Échelle réelle',
  'builder.true-scale-title':
    'Afficher le plateau à sa taille réelle, dans la mesure où cet écran déclare sa propre densité',
  'builder.symmetry': 'Symétrie',
  'builder.symmetry-on': 'Aligner sur l’axe central et sur les éléments en miroir',
  'builder.symmetry-off': 'Cesser d’aligner sur l’axe central',
  'builder.reachability': 'Accessibilité',
  'builder.reachability-on': 'Montrer où la bille peut et ne peut pas aller',
  'builder.reachability-off': 'Masquer un instant l’ombrage d’accessibilité',
  'builder.undo': 'Annuler',
  'builder.undo-title': 'Annuler la dernière modification',
  'builder.redo': 'Rétablir',
  'builder.open-file': 'Ouvrir un fichier',
  'builder.open-file-title': 'Ouvrir un fichier JSON de machine, par exemple écrit par le serveur MCP',
  'builder.redo-title': 'Rétablir la modification annulée',
  'builder.test-play': 'Essayer',
  'builder.test-play-title': 'Mettre une bille sur cette machine, ici, au vrai 1000 Hz (Espace)',
  'builder.new-machine': 'Commencer une nouvelle machine',
  'builder.discard-title': 'Cliquez de nouveau pour jeter cette machine et repartir du modèle',
  'builder.discard-confirm': 'Jeter et recommencer?',
  'builder.reset-default': 'Revenir à la machine par défaut',
  'builder.reset-default-title':
    'Jeter cette machine et revenir à NEON NOVA. La porte d’entrée y revient aussi',
  'builder.reset-confirm': 'Jeter cette machine?',
  'builder.kept': 'Conservée dans ce navigateur. La porte d’entrée peut la jouer.',
  'builder.add-device': 'Ajouter un élément',
  // Les six groupes de la palette (M13-12), dans l'ordre des tuiles du plateau du casque.
  'builder.category-flippers': 'Flippers',
  'builder.category-bumpers': 'Bumpers et élastiques',
  'builder.category-targets': 'Cibles',
  'builder.category-lanes': 'Couloirs et portillons',
  'builder.category-ball': 'Gestion de la bille',
  'builder.category-fields': 'Champs',
  'builder.remove': 'Retirer',
  'builder.remove-none': 'Sélectionnez un élément pour le retirer',
  'builder.devices': 'Éléments sur le plateau',
  'builder.select-device': 'Sélectionner {id} ({kind})',
  'builder.select-shape': 'Forme {id} ({kind})',
  'builder.nothing-selected':
    'Rien de sélectionné. Cliquez sur un élément dans le canevas ou dans la liste.',
  'builder.positioned-by-targets': 'Positionné par ses cibles.',
  'builder.nothing-wrong': 'Rien à signaler',
  'builder.modes-lede':
    'Un mode est un ensemble de tirs et ce qui se produit à mesure qu’ils sont réussis. Choisissez les tirs sur la carte.',
  'builder.display-lede':
    'Une scène est une liste d’opérations de générateur dessinées sur la grille de points à chaque pas, ce qui la fait bouger. L’aperçu la joue au rythme que voit un joueur, avec le lecteur d’afficheur du jeu.',
  'builder.speech-lede':
    'Une annonce est composée de sons plutôt que de texte, afin que la machine dise exactement ce qu’on lui a demandé. Le sous-titre à côté est ce que lit un joueur, et il est obligatoire.',
  'builder.rules-lede':
    'Chaque référence est un sélecteur parmi ce que cette machine déclare, afin qu’une règle ne puisse pas nommer quelque chose qui n’existe pas.',

  // -------------------------------------------------------------------------
  // L’éditeur d’afficheur
  // -------------------------------------------------------------------------
  'display.none':
    'Cette machine ne déclare aucun afficheur, il n’y a donc rien à composer. Partez d’un modèle.',
  'display.playback': 'Lecture',
  'display.scrub': 'Parcourir la scène',
  'display.play': 'Lire',
  'display.pause': 'Pause',
  'display.play-title': 'Lire la scène à son rythme réel',
  'display.pause-title': 'Garder la scène où elle est',
  'display.palette': 'Palette',
  'display.palette-title': 'La seule couleur dans laquelle tout l’afficheur s’illumine',
  'display.scenes': 'Scènes',
  'display.add-scene': 'Ajouter une scène',
  'display.add-scene-title': 'Ajouter une scène portant une ligne de texte',
  'display.remove-scene': 'Retirer la scène',
  'display.remove-scene-title': 'Retirer cette scène, si rien ne l’affiche',
  'display.this-scene': 'Cette scène',
  'display.loops': 'En boucle',
  'display.loops-title':
    'Tourne jusqu’à ce qu’autre chose s’affiche, au lieu de rendre la main au score',
  'display.loops-hint':
    'Une scène en boucle tourne jusqu’à ce qu’autre chose s’affiche. Une scène minutée rend la main à l’afficheur du score quand elle se termine.',
  'display.runs-for': 'Durée',
  'display.runs-for-title': 'Combien de temps avant que l’afficheur rende la main au score',
  'display.layout': 'Disposition',
  'display.layout-title': 'Pour combien de joueurs l’afficheur du score est disposé',
  'display.score-scene':
    'Une scène de score n’a pas d’images : elle est dessinée à partir des scores et du numéro de bille du jeu.',
  'display.score-scene-title': 'Une scène de score, dessinée à partir du jeu plutôt que d’images',
  'display.frames': 'Images',
  'display.frames-hint':
    'Dessinées dans l’ordre sur un afficheur effacé, à chaque pas. La dernière est au-dessus.',
  'display.frame-of': 'Image {index} sur {total}',
  'display.later-title': 'Dessiner celle-ci plus tard, pour qu’elle passe par-dessus davantage',
  'display.earlier-title': 'Dessiner celle-ci plus tôt, pour que davantage passe par-dessus',
  'display.copy': 'Copier',
  'display.copy-title': 'Faire une copie de cette image juste après elle',
  'display.remove': 'Retirer',
  'display.remove-frame-title': 'Retirer cette image',
  'display.add-frame': 'Ajouter une image',
  'display.add-op-title': 'Ajouter un(e) {op} par-dessus',
  'display.pick-frame': 'Choisissez une image pour changer ce qu’elle dessine.',
  'display.canvas-title': 'La scène {scene}, {wide} sur {tall} points',
  'display.absent-hint': '{absent} — laisser le moteur le placer',
  'display.column-hint': '{label} : quelle colonne, comptée depuis la gauche',
  'display.row-hint': '{label} : quelle ligne, comptée depuis le haut',
  'display.pair-hint': '{label} — {hint}',
  'display.invert-hint': 'Retourne chaque point. Il n’y a rien à régler dessus.',

  // -------------------------------------------------------------------------
  // L’éditeur d’illustration
  // -------------------------------------------------------------------------
  'art.none':
    'Cette machine n’a pas encore de bloc d’illustration, il n’y a donc rien sur quoi dessiner. Partez d’un modèle.',
  'art.surfaces': 'Surfaces',
  'art.layers': 'Couches',
  'art.layers-hint':
    'Peintes de bas en haut : la dernière est dessinée par-dessus toutes les précédentes.',
  'art.layer-of': 'Couche {index} sur {total}',
  'art.up-title': 'Monter cette couche, pour qu’elle peigne par-dessus davantage',
  'art.down-title': 'Descendre cette couche, pour que davantage peigne par-dessus',
  'art.copy': 'Copier',
  'art.copy-title': 'Faire une copie de cette couche juste au-dessus',
  'art.remove': 'Retirer',
  'art.remove-title': 'Retirer cette couche',
  'art.add-layer': 'Ajouter une couche',
  'art.add-op-title': 'Ajouter un(e) {op} au sommet de la pile',
  'art.seed': 'Graine de la machine',
  'art.seed-title': 'Le nombre dont s’inspire chaque couche aléatoire',
  'art.seed-hint':
    'Un seul nombre pour toute la machine. Chaque champ d’étoiles et chaque champ de traînées s’en inspire, dans l’ordre du programme — donc le relancer les remélange tous d’un coup, et ajouter une couche sous un champ d’étoiles change ce que ce champ dessine. C’est la règle du format, pas celle de ce panneau.',
  'art.params': 'Paramètres de la couche',
  'art.pick-layer': 'Choisissez une couche pour changer ce qu’elle dessine.',
  'art.surface-title': '{label} — {count} couches',
  'art.surface-title-one': '{label} — 1 couche',
  'art.layer-count': '{count} couches',
  'art.layer-count-one': '1 couche',
  'art.across-hint': '{label} : en largeur sur la surface, 0 à gauche et 1 à droite',
  'art.up-hint': '{label} : en hauteur sur la surface, 0 en bas et 1 en haut',
  'art.pair-hint': '{label} — {hint}',
  'art.corner-hint': 'Bord {corner} de la région',
  'art.first-letter': 'La couleur de la première lettre',
  'art.last-letter': 'La couleur de la dernière lettre',
  'art.height-hint': 'La hauteur descend dans l’image, 0 en haut.',
  'art.corner.left': 'Gauche',
  'art.corner.near': 'Proche',
  'art.corner.right': 'Droite',
  'art.corner.far': 'Éloigné',

  // -------------------------------------------------------------------------
  // L’éditeur audio
  // -------------------------------------------------------------------------
  'audio.cues': 'Signaux',
  'audio.add-cue': 'Ajouter un signal',
  'audio.add-cue-title': 'Ajouter un signal qu’une règle peut jouer',
  'audio.remove-cue': 'Retirer le signal',
  'audio.remove-cue-title': 'Retirer ce signal, si aucune règle ne le joue',
  'audio.voice': 'La voix',
  'audio.voice-hint':
    'La voix à formants dans laquelle chaque annonce est prononcée. Les mots eux-mêmes relèvent de l’éditeur de phonèmes.',
  'audio.voice-pitch': 'Hauteur de la voix',
  'audio.voice-pitch-title': 'À quelle hauteur parle la voix des annonces',
  'audio.none':
    'Cette machine ne déclare aucun signal. Ajoutez-en un, et une règle pourra le jouer.',
  'audio.play': 'Écouter',
  'audio.play-title': 'Entendre ce signal exactement comme le jeu le jouerait',
  'audio.silent': 'Il n’y a rien à écouter : ce signal ne produit aucun son',
  'audio.synth': 'Synthèse',
  'audio.synth-title': 'Quelle recette du document 07',
  'audio.notes': 'Notes',
  'audio.add-note': 'Ajouter une note',
  'audio.add-note-title': 'Frapper une lame de plus avec ce signal',
  'audio.remove-note': 'Retirer une note',
  'audio.remove-note-title': 'En frapper une de moins',
  'audio.note-title': 'Note {index}, en hertz',
  'audio.notes-hint':
    'En hertz. Le jeu compte trois lames — 660, 880 et 1320 — et une note entre elles est jouée en transposant la plus proche, ce qu’est un vrai jeu de carillons : une lame est un ensemble de partiels inharmoniques en rapport fixe, donc la transposer revient à la même lame coupée à une autre longueur. Sans aucune note, le signal frappe la lame du milieu.',
  'audio.let-engine': 'Laisser le moteur décider',
  'audio.no-rule': 'Aucune règle ne joue encore ce signal.',

  // -------------------------------------------------------------------------
  // Intitulés des surfaces d’illustration
  // -------------------------------------------------------------------------
  'art.surface-label': '{label} — {layers}, {wide} × {tall}',
  'art.canvas-label': '{layers}, dessinées en {wide} sur {tall}',

  // -------------------------------------------------------------------------
  // Intitulé de la recette audio
  // -------------------------------------------------------------------------
  'audio.synth-full-title': 'Quelle recette du catalogue du document 07 produit ce son',

  // -------------------------------------------------------------------------
  // L’éditeur de règles
  // -------------------------------------------------------------------------
  'rules.list': 'Règles',
  'rules.add': 'Ajouter une règle',
  'rules.add-title': 'Ajouter une règle qui s’exécute au lancement d’une bille',
  'rules.open-title': 'Ouvrir {id} — {on}',
  'rules.pick': 'Choisissez une règle pour voir ce qu’elle fait.',
  'rules.remove': 'Retirer cette règle',
  'rules.remove-title': 'Retirer {id} de la machine',
  'rules.json-title': 'Le JSON de {id}',
  'rules.show-graph': 'Afficher le graphe',
  'rules.hide-graph': 'Masquer le graphe',
  'rules.show-graph-title': 'Montrer comment ces règles s’enchaînent, et toute boucle entre elles',
  'rules.hide-graph-title': 'Masquer le graphe de l’enchaînement de ces règles',
  'rules.sheet': 'Feuille de règles',
  'rules.read-sheet': 'Lire la feuille de règles',
  'rules.hide-sheet': 'Masquer la feuille de règles',
  'rules.read-sheet-title':
    'Lire cette machine comme le fera un joueur — générée à partir des règles elles-mêmes',
  'rules.show-json': 'Afficher le JSON',
  'rules.hide-json': 'Masquer le JSON',
  'rules.show-json-title': 'Afficher le JSON qu’ont produit ces blocs',
  'rules.hide-json-title': 'Masquer le JSON qu’ont produit ces blocs',
  'rules.deep':
    'Cette condition est plus profonde que ce que les blocs dessinent, elle est donc conservée telle quelle. Le JSON ci-dessous en est la vérité.',
  'rules.trigger-kind': 'Quel genre de chose déclenche cette règle',
  'rules.which-ball': 'Quel évènement de bille',
  'rules.which-edge': 'Quel front',
  'rules.all-or-any': 'Si chaque ligne doit être vraie, ou n’importe laquelle',
  'rules.comparison': 'Comment les deux côtés sont comparés',
  'rules.remove-condition': 'Retirer cette condition',
  'rules.add-condition': 'Ajouter une condition que cette règle doit satisfaire',
  'rules.side-kind': 'Si ce côté est une variable ou un nombre',
  'rules.number': 'Le nombre auquel comparer',

  // -------------------------------------------------------------------------
  // L’éditeur de voix
  // -------------------------------------------------------------------------
  'speech.callouts': 'Annonces',
  'speech.add': 'Ajouter une annonce',
  'speech.add-title': 'Ajouter une annonce',
  'speech.remove': 'Retirer l’annonce',
  'speech.remove-title': 'Retirer cette annonce, si aucune règle ne la prononce',
  'speech.none':
    'Cette machine n’a aucune annonce. Ajoutez-en une, et une règle pourra la prononcer.',
  'speech.say-it': 'La dire',
  'speech.say-it-title': 'Entendre cette annonce dans la voix de la machine',
  'speech.nothing-to-say': 'Il n’y a encore aucun phonème, il n’y a donc rien à dire',
  'speech.caption': 'Sous-titre',
  'speech.caption-title': 'Ce qu’un joueur lit à l’écran pendant que cela est prononcé',
  'speech.caption-hint':
    'Obligatoire. Jamais prononcé — c’est ce que lit un joueur, et les mots ci-dessous sont ce que dit la machine.',
  'speech.sequence': 'Séquence',
  'speech.sequence-empty': 'Rien pour l’instant. Choisissez un son ci-dessous.',
  'speech.remove-sound': 'Retirer {name}',
  'speech.clear': 'Effacer',
  'speech.clear-title': 'Retirer tous les sons et recommencer la ligne',
  'speech.emphasis': 'Accentuation',
  'speech.stress-on': 'Accentuer cette syllabe',
  'speech.stress-off': 'Retirer l’accentuation',
  'speech.emphasis-hint':
    'Une syllabe accentuée est tenue plus longtemps et montée en hauteur. Une marque suit la syllabe qu’elle porte, donc retirer un son ailleurs dans la ligne la laisse où vous l’avez mise; retirer la voyelle de cette syllabe supprime la marque plutôt que de la déplacer sur une voisine.',
  'speech.phoneme-title': '{name} comme dans {example} — {ms} ms',
  'speech.arpabet':
    'ARPAbet, l’alphabet que nomme 07 §4. Les majuscules de chaque exemple sont les lettres qui produisent le son.',
  'speech.no-rule': 'Aucune règle ne prononce encore cette annonce.',

  // -------------------------------------------------------------------------
  // Le panneau de publication
  // -------------------------------------------------------------------------
  'publish.heading': 'Enregistrer et publier',
  'publish.sign-in':
    'Connectez-vous pour enregistrer cette machine. Vous pouvez continuer à construire sans compte — rien n’est perdu à attendre.',
  'publish.drafts-note':
    'Les brouillons sont à vous et restent privés. Publier place une version dans l’arcade, figée pour toujours, parce que des scores y sont rattachés.',
  'publish.save-draft': 'Enregistrer le brouillon',
  'publish.save-draft-title': 'Enregistrer {semver} comme brouillon privé',
  'publish.publish': 'Publier',
  'publish.publish-pro': 'Publier — Pro',
  'publish.publish-title': 'Publier {semver} dans l’arcade',
  'publish.publish-pro-title': 'Publier dans l’arcade fait partie de Pro',
  'publish.headset': 'Ouvrir dans un casque',
  'publish.headset-pro': 'Ouvrir dans un casque — Pro',
  'publish.headset-title': 'Enregistrer {semver} et obtenir un code pour l’ouvrir dans un Quest',
  'publish.headset-pro-title': 'Ouvrir un brouillon dans un casque fait partie de Pro',
  'publish.headset-here': 'Ouvrir dans ce casque',
  'publish.headset-here-title': 'Jouer votre machine sur la page d’accueil et y passer en RM ou en RV',
  'publish.opening-here': 'Conservation de votre machine, puis direction la page d’accueil…',
  'publish.draft-not-kept':
    'Votre machine n’a pas pu être conservée dans ce navigateur ; le casque n’a donc rien à ouvrir. Réessayez.',
  'publish.saving': 'Enregistrement…',
  'publish.saved': 'Brouillon {semver} enregistré.',
  'publish.saving-code': 'Enregistrement, puis création d’un code…',
  'publish.publishing': 'Publication…',
  'publish.published': 'Version {semver} publiée. Elle est maintenant dans l’arcade.',
  'publish.review':
    'Envoyée en révision. Une personne lit une première publication avant qu’elle n’atteigne l’arcade.',
  'publish.no-machine': 'La machine n’a pas pu être créée.',
  'publish.no-version':
    'Le brouillon est enregistré, mais le serveur n’a pas indiqué quelle version.',
  'publish.no-code': 'Le serveur n’a pas renvoyé de code.',
  'publish.quest-choose': 'Dans l’application Quest, choisissez',
  'publish.open-draft': 'Ouvrir un brouillon',
  'publish.ten-minutes': 'Il dure environ dix minutes.',
  'publish.stops-at': 'Il cesse de fonctionner à {time}.',

  // -------------------------------------------------------------------------
  // Essai en direct
  // -------------------------------------------------------------------------
  'test.tools': 'Outils d’essai',
  'test.transport': 'Figer et avancer',
  'test.tools-open': 'Outils',
  'test.tools-open-title':
    'Figer, avancer, ralenti, le plan de bille et les bancs d’essai, par-dessus la machine',
  'test.tools-close-title': 'Ranger les outils et rendre l’écran à la machine',
  'test.stop': 'Arrêter',
  'test.stop-title': 'Arrêter la machine et revenir à l’édition (Échap)',
  'test.step': 'Pas',
  'test.step-title':
    'Avancer d’un pas de simulation, soit une milliseconde de cette machine',
  'test.freeze': 'Figer',
  'test.run': 'Reprendre',
  'test.freeze-title': 'Arrêter la simulation où elle en est',
  'test.run-title': 'Laisser la machine repartir',
  'test.speed': 'Vitesse',
  'test.speed-title':
    'À quelle vitesse le temps s’écoule. Le taux de simulation ne change pas.',
  'test.slow-motion': 'Ralenti',
  'test.speed-hint':
    'La commande de vitesse change la vitesse à laquelle le temps s’écoule devant vous et',
  'test.place-ball': 'Cliquez pour poser la bille ici, immobile',
  'test.starting': 'Démarrage…',

  // -------------------------------------------------------------------------
  // L’éditeur de modes
  // -------------------------------------------------------------------------
  'modes.list': 'Modes',
  'modes.add': 'Ajouter un mode',
  'modes.open-title': 'Ouvrir {title}',
  'modes.pick': 'Choisissez un mode, ou ajoutez-en un.',
  'modes.remove': 'Retirer ce mode',
  'modes.remove-title': 'Retirer {title} de la machine',
  'modes.no-objectives':
    'Aucun objectif pour l’instant. Cliquez sur un élément de la carte pour en faire un.',
  'modes.remove-shot-title': 'Retirer {shot} de ce mode',
  'modes.stackable-on': 'Laisser ce mode tourner en même temps qu’un autre',
  'modes.stackable-off': 'Empêcher ce mode de tourner en même temps qu’un autre',

  // -------------------------------------------------------------------------
  // Le banc de tir
  // -------------------------------------------------------------------------
  'harness.heading': 'Banc de tir',
  'harness.settings': 'Réglages du banc',
  'harness.which-flipper': 'Depuis quel palet tirer',
  'harness.left': 'Palet gauche',
  'harness.right': 'Palet droit',
  'harness.coil': 'La bobine, en fraction de ce que déclare la machine',
  'harness.stop': 'Arrêter',
  'harness.stop-title': 'Cesser de tirer',
  'harness.not-run': 'non exécuté',
  'harness.done': 'terminé',

  // -------------------------------------------------------------------------
  // Le reste du Créateur
  // -------------------------------------------------------------------------
  'actions.add': 'Ajouter une action…',
  'actions.add-title': 'Ajouter quelque chose à faire ici',
  'actions.remove-title': 'Retirer cette action',
  'actions.number-title': 'Le nombre qu’utilise cette action',
  'actions.value-title': 'L’une des valeurs que permet cette action',
  'flipper.heading': 'Géométrie des palets',
  'flipper.figure': 'Géométrie des palets (PH-054)',
  'flipper.select': 'Sélectionner {id}',
  'flipper.no-overlap': 'aucune interpénétration',
  'flipper.crossing': 'les batteurs se croisent, il n’y a donc aucun drain central',
  'force.heading': 'Forcer un état',
  'force.none':
    'Cette machine ne déclare aucun mode, il n’y en a donc aucun où sauter.',
  'force.start': 'Démarrer',
  'force.end': 'Terminer',
  'height.too-few': 'Deux points font une pente. Celle-ci en a moins.',
  'height.steepest': 'La plus raide',
  'inspector.pick': 'Sélectionnez un élément pour voir ce qui peut y être réglé.',
  'inspector.nothing':
    'Rien à régler sur celui-ci — il est placé et formé, et c’est tout ce qu’il a.',
  'inspector.properties': 'Propriétés de {id}',
  'inspector.range': '{label} — entre {min} et {max}',
  'inspector.set': 'Défini',
  'inspector.default': 'Par défaut',
  'canvas.label':
    'Disposition du plateau. Faites glisser un élément pour le déplacer; les touches fléchées déplacent l’élément sélectionné d’un millimètre.',
  'ramp.hint':
    'Faites glisser un point sur le plateau pour tracer la rampe; réglez ici la hauteur qu’elle atteint.',
  'ramp.shape-of': 'Forme de {id}',
  'ramp.select-point': 'Sélectionner le point {index} sur {total}',
  'ramp.height-of': 'Hauteur du point {index}, en millimètres au-dessus du plateau',
  'ramp.height-range': 'Hauteur du point {index} en millimètres, entre {min} et {max}',
  'ramp.add-point': 'Ajouter un point à mi-chemin du suivant, sans changer la forme',
  'ramp.remove': 'Retirer {id}',
  'ramp.remove-title': 'Retirer {id} de la machine, avec ses interrupteurs et ses supports. Annuler la ramène.',
  'graph.none': 'Aucune règle pour l’instant, donc rien ne s’enchaîne à rien.',
  'graph.unguarded':
    'Chaque règle de cette boucle se déclenche sans condition, elle tournera donc jusqu’à ce que le moteur l’interrompe',
  'graph.mode-hooks': 'points d’ancrage de mode',
  'graph.loop': 'Boucle',
  'graph.guarded-loop': 'Boucle protégée',
  'harness.fire': 'Tirer 200',
  'shotmap.label':
    'Carte du plateau. {count} tirs choisis. Cliquez sur un élément pour l’ajouter ou le retirer.',
  'soak.settings': 'Réglages du test d’endurance',
  'soak.how-many': 'Combien de billes jouer',
  'soak.run': 'Endurance',
  'soak.stop': 'Arrêter',
  'soak.stop-title': 'Arrêter l’exécution',
  'soak.not-run': 'non exécuté',
  'soak.hint':
    'Joue des billes sans affichage avec un robot qui martèle les palets, et rapporte ce qui est resté coincé. Une bille sur dix mille qui se gare dans une poche, c’est une partie sur quelques centaines qui se termine avec la machine à la recherche de sa bille — et vous ne le voyez jamais, parce que cela ne vous arrive jamais.',
  'validation.all-pass':
    'Chaque classe contre laquelle cette machine peut être vérifiée est réussie.',
  'validation.run-soak': 'lancer le test d’endurance',

  // -------------------------------------------------------------------------
  // Arêtes du graphe et passage au Quest
  // -------------------------------------------------------------------------
  'graph.edge-title': '{from} → {to} ({rules})',
  'publish.quest-tail': 'et saisissez ce code. Il ouvre',

  // -------------------------------------------------------------------------
  // Le reste de l’habillage
  // -------------------------------------------------------------------------
  'soak.play-title': 'Jouer {count} billes',
  'common.loading': 'Chargement…',
  'notfound.heading': 'Rien à cette adresse',
  'notfound.on-the-way': 'C’est en route. D’ici là, une machine vous attend.',
  'test.speed-not': 'pas',
  'test.speed-hint-tail':
    'la simulation : un pas vaut une milliseconde de machine à chaque réglage, donc ce que vous voyez à 0,2x est exactement ce qui se produit à plein régime, cinq fois plus lentement.',

  // -------------------------------------------------------------------------
  // Verdicts des classes de validation
  // -------------------------------------------------------------------------
  'validation.refuses': 'refuse',
  'validation.warns': 'avertit',
  'validation.passes': 'réussit',

  // -------------------------------------------------------------------------
  // Navigation, titres de page et bouton d’apparence
  // -------------------------------------------------------------------------
  'nav.tab.arcade': 'Arcade',
  'nav.tab.boards': 'Classements',
  'nav.tab.play': 'Jouer',
  'nav.tab.build': 'Créer',
  'nav.tab.you': 'Vous',
  'nav.tab.arcade-title': 'Parcourir les machines',
  'nav.tab.boards-title': 'Classements',
  'nav.tab.play-title': 'Jouer à NEON NOVA',
  'nav.tab.build-title': 'Le Créateur de machines',
  'nav.tab.you-title': 'Vos scores et vos paramètres',
  'nav.back-label': 'Retour',
  'route.title.play': 'NEON NOVA',
  'route.title.arcade': 'Arcade',
  'route.title.play-machine': 'Jouer',
  'route.title.machine-guide': 'Guide de la machine',
  'route.title.settings': 'Paramètres',
  'route.title.headset': 'Ouvrir sur votre casque',
  'route.title.boards': 'Classements',
  'route.title.account': 'Compte',
  'route.title.watch': 'Regarder',
  'theme.system': 'Suit votre appareil — actuellement en mode {appearance}',
  'theme.always': 'Toujours {choice}',
  'theme.appearance.light': 'clair',
  'theme.appearance.dark': 'sombre',

  // -------------------------------------------------------------------------
  // Les autres titres de page
  // -------------------------------------------------------------------------
  'route.title.link-headset': 'Jumeler un casque',
  'boards.friends-link': 'Amis, présence et invitations',
  'friends.profile-title': 'Ouvrir leur page',
  'friends.lede': 'Demandez par pseudo, répondez à ce qu’on vous demande, et voyez qui est là.',
  'friends.needs-account': 'Les amis exigent un compte — la liste est sur le serveur, pas dans ce navigateur.',
  'friends.account': 'Aller à votre compte',
  'friends.account-title': 'Ouvrir la page du compte',
  'friends.online': 'dans l’arcade',
  'friends.invite': 'Inviter à une machine',
  'friends.invite-title': 'Lancer une partie et leur envoyer le lien',
  'friends.invite-copied': 'Lien copié. Envoyez-le-leur : il les assoit à la machine où vous êtes.',
  'friends.invite-failed': 'Impossible de lancer une partie pour l’invitation. Réessayez dans un instant.',

  'history.heading': 'Vos parties',
  'history.lede': 'Toutes les parties terminées dans ce navigateur. Celles qui ont atteint un classement se revoient.',
  'history.loading': 'Lecture du navigateur…',
  'history.empty': 'Rien encore. Jouez une bille et ce sera ici.',
  'history.play': 'Jouer',
  'history.play-title': 'Aller à la machine',
  'history.caption': '{count} parties terminées',
  'history.machine': 'Machine',
  'history.score': 'Pointage',
  'history.when': 'Quand',
  'history.where': 'Conservée',
  'history.watch': 'Revoir',
  'history.watch-title': 'Revoir cette partie',
  'history.origin.posted': 'Au classement',
  'history.origin.queued': 'En attente d’envoi',
  'history.origin.refused': 'Refusée',
  'history.origin.local': 'Ce navigateur seulement',

  'profile.heading': 'Joueur',
  'profile.loading': 'Recherche en cours…',
  'profile.missing': 'Aucun joueur nommé « {handle} ».',
  'profile.boards': 'Aller aux classements',
  'profile.boards-title': 'Ouvrir les classements',
  'profile.progress': 'Au classement de {reached} machines sur {total}.',
  'profile.best': 'Meilleurs pointages',
  'profile.no-scores': 'Aucun pointage à un classement pour l’instant.',
  'profile.machines': 'Machines publiées',
  'profile.no-machines': 'Aucune machine publiée.',

  'tournament.heading': 'Tournoi',
  'tournament.loading': 'Demande du tournoi de la semaine…',
  'tournament.none': 'Aucun tournoi en cours. Le prochain ouvre à la fermeture de celui-ci.',
  'tournament.needs-account': 'Un tournoi exige un compte — une inscription est une partie validée, et une partie a besoin d’un endroit où être validée.',
  'tournament.window': 'Ouvert du {opens} au {closes}.',
  'tournament.fixed': 'Germe fixe {seed}, réglages {settings}.',
  'tournament.core': 'Cœur de simulation v{version}.',
  'tournament.core-mismatch':
    'Ce tournoi rejoue sur le cœur v{version}, mais ce client joue sur v{ours} — les parties jouées ici ne peuvent pas y participer.',
  'tournament.defaults':
    'Tout le monde joue les réglages par défaut de la machine. Une partie jouée avec un réglage d’exploitant modifié est classée sur son propre tableau, pas celui-ci.',
  'tournament.play': 'Jouer la machine',
  'tournament.play-title': 'Aller jouer une partie pour participer',
  'tournament.nothing-to-enter': 'Rien à inscrire pour l’instant — terminez une partie et laissez-la atteindre un classement.',
  'tournament.enter': 'Inscrire votre {score}',
  'tournament.enter-title': 'Inscrire votre meilleure partie validée',
  'tournament.entered': 'Inscrite. Votre partie est au classement.',
  'tournament.standings': 'Classement',
  'tournament.empty': 'Personne ne s’est encore inscrit.',

  // La file de modération (M9-28, 11 §6).
  // Le remix (M9-46, 11 §7).
  'remix.looking-at':
    'Vous regardez {title}, de {author}. Faites-en un remix pour conserver vos modifications.',
  'remix.take': 'En faire une machine à moi',
  'remix.take-title':
    'Créer une nouvelle machine qui vous appartient, en gardant sa provenance',
  'remix.needs-pro':
    'Conserver une machine fait partie de Pro. Vous pouvez toujours modifier et essayer celle-ci.',
  'remix.needs-account':
    'Un remix exige un compte, car la nouvelle machine appartient à quelqu’un.',
  'remix.failed': 'Impossible de faire ce remix. Réessayez dans un instant.',

  // Le signalement (M9-28, 11 §6).
  'stage.menu.report': 'Signaler cette machine',
  'stage.menu.report-title': 'Dire à un modérateur que quelque chose ne va pas',
  'report.heading': 'Signaler cette machine',
  'report.about': 'Un modérateur lit chaque signalement. Il s’agit de {title}.',
  'report.reason-legend': 'Qu’est-ce qui ne va pas ?',
  'report.reason.offensive-text': 'Texte offensant',
  'report.reason.real-machine': 'Une copie d’une machine réelle',
  'report.reason.broken': 'Elle ne fonctionne pas',
  'report.reason.misleading-rating': 'La classification est erronée',
  'report.reason.other': 'Autre chose',
  'report.detail': 'Autre chose à savoir (facultatif)',
  'report.detail-title': 'Jusqu’à 400 caractères, visibles uniquement par les modérateurs',
  'report.send': 'Envoyer le signalement',
  'report.send-title': 'Envoyer ceci à un modérateur',
  'report.close': 'Fermer',
  'report.close-title': 'Fermer sans envoyer',
  'report.sent': 'Merci. Un modérateur le lira.',
  'report.needs-account':
    'Un signalement exige un compte, pour qu’une seule personne ne puisse pas ressembler à une foule.',
  'report.failed': 'Impossible d’envoyer. Réessayez dans un instant.',

  'keys.heading': 'API du Créateur et outils IA',
  'keys.lead':
    'Une clé permet à un outil ou à une IA de construire des machines sur ce compte. Téléchargez le serveur MCP, puis créez-lui une clé.',
  'keys.shown-once': 'C’est la seule fois où vous verrez cette clé. Copiez-la maintenant.',
  'keys.copy': 'Copier',
  'keys.copied': 'Copiée.',
  'keys.copied-title': 'Copier la clé dans le presse-papiers',
  'keys.done': 'Terminé',
  'keys.done-title': 'Masquer la clé',
  'keys.label': 'À quoi sert cette clé ?',
  'keys.label-title': 'Un nom pour cette clé, 40 caractères au plus',
  'keys.label-example': 'Claude Code sur le PC du bureau',
  'keys.scopes': 'Ce qu’elle peut faire',
  'keys.scope.read': 'Lire mes machines',
  'keys.scope.write': 'Créer et enregistrer des brouillons',
  'keys.scope.publish': 'Publier dans l’arcade',
  'keys.new': 'Créer une clé',
  'keys.new-pro': 'Créer une clé (Pro)',
  'keys.new-title': 'Créer une nouvelle clé d’API',
  'keys.looking': 'Recherche de vos clés…',
  'keys.none': 'Aucune clé pour l’instant.',
  'keys.too-many': 'Vous avez déjà cinq clés actives. Révoquez-en une pour en créer une autre.',
  'keys.revoke': 'Révoquer',
  'keys.revoke-confirm': 'Oui, révoquer',
  'keys.revoke-question': 'Révoquer {label} ? Tout outil qui l’utilise s’arrête aussitôt.',
  'keys.revoke-title': 'Révoquer {label}',
  'keys.revoked': '{label} a été révoquée.',
  'keys.keep': 'Garder',
  'keys.keep-title': 'Garder cette clé',
  'package.get': 'Obtenir le lien de téléchargement',
  'package.get-title': 'Demander un lien vers le fichier du serveur MCP ; il dure dix minutes',
  'package.save': 'Enregistrer {file}',
  'package.save-title': 'Un seul fichier JavaScript ; le lien dure dix minutes',
  'package.sha': 'Empreinte SHA-256 {digest}',
  'package.node': 'Nécessite Node 22 ou plus récent',
  'package.how': 'Comment le connecter à Claude, Cursor ou un autre agent',
  'package.none': 'Aucun paquet n’a encore été publié.',
  'moderation.heading': 'File de modération',
  'moderation.lede':
    'Les plus anciennes d’abord. Rejeter la plainte publie la machine ; la retenir la renvoie à l’auteur sous forme de brouillon qu’il peut corriger.',
  'moderation.loading': 'Demande de la file…',
  'moderation.empty': 'Rien n’attend. Chaque publication a été lue.',
  'moderation.needs-account': 'La file exige un compte.',
  'moderation.not-a-moderator': 'Cette page est réservée aux modérateurs.',
  'moderation.by': 'Par {author}, qui a déjà publié {published} fois. En attente depuis {opened}.',
  'moderation.author-unknown': 'un auteur sans pseudonyme',
  'moderation.why': 'En file parce que : {reasons}. Signalements : {reports}.',
  'moderation.screen-clean': 'Le filtre automatique n’a rien relevé de mécanique.',
  'moderation.reason.first-publication': 'première publication',
  'moderation.reason.screen-findings': 'le filtre a relevé quelque chose',
  'moderation.reason.reported': 'signalée par un joueur',
  'moderation.reason.sampled': 'échantillonnée',
  'moderation.open': 'Ouvrir dans le Créateur',
  'moderation.open-title': 'Regarder la machine avant de décider',
  'moderation.notes': 'Notes pour le prochain modérateur',
  'moderation.notes-title': 'Conservées pour les modérateurs, jamais montrées à l’auteur',
  'moderation.dismiss': 'Rien à signaler — publier',
  'moderation.dismiss-title': 'Rejeter la plainte et mettre la machine dans l’arcade',
  'moderation.uphold': 'Il y a un problème — la renvoyer',
  'moderation.uphold-title':
    'Retenir la plainte, avertir l’auteur, ramener la version au brouillon',
  'moderation.takedown': 'Retirer',
  'moderation.takedown-title':
    'Retirer immédiatement cette version publiée ; les scores sont conservés',
  'moderation.decided': '{title} est maintenant : {status}.',
  'moderation.taken-down': '{title} a été retirée.',
  'moderation.saved-from-tool': 'enregistrée depuis un outil',
  'moderation.saved-from-headset': 'enregistrée depuis un casque',
  'moderation.block': 'Bloquer l’auteur',
  'moderation.block-title': 'Empêcher cet auteur de créer des machines',
  'moderation.block-reason': 'Pourquoi cet auteur est bloqué',
  'moderation.block-reason-title': 'Conservé avec le blocage, pour le prochain modérateur ; 200 caractères au plus',
  'moderation.block-confirm': 'Bloquer',
  'moderation.block-keep': 'Garder',
  'moderation.block-keep-title': 'Ne pas bloquer cet auteur',
  'moderation.blocked': 'Bloqué. Ses clés ont été révoquées et les routes du Créateur le refusent.',
  'pricing.heading': 'Ce que ça coûte',
  'pricing.lede':
    'NEON NOVA est gratuite, sans compte, pour toujours. Pro, c’est tout le reste — la sélection, le concepteur, le casque, l’arcade.',
  'pricing.annual': 'Un an de Pro',
  'pricing.monthly': 'Un mois de Pro',
  'pricing.per-month': '{amount} par mois, facturé à l’année',
  'pricing.saving': 'Économisez {percent} %',
  'pricing.included': 'Ce que Pro ajoute',
  'pricing.free-forever':
    'Un compte gratuit garde vos pointages, votre historique et vos reprises, et vous place aux classements. Ça n’expire pas et ça ne coûte rien.',
  'pricing.cta': 'Prendre Pro',
  'pricing.cta-title': 'Aller à la page du compte et s’abonner',
  'pricing.play': 'Jouer gratuitement plutôt',
  'route.title.pricing': 'Tarifs',
  'route.title.friends': 'Amis',
  'route.title.history': 'Vos parties',
  'route.title.profile': 'Joueur',
  'route.title.tournaments': 'Tournoi',
  'route.title.moderation': 'Modération',
  'route.title.builder-machine': 'Concepteur de machines',
  'route.title.builder': 'Créateur de machines',
  'arcade.pricing-link': 'Le prix de Pro',
  'paywall.pricing-link': 'Voir tous les tarifs, et ce qui est gratuit pour toujours',
  'route.title.how-to-play': 'Comment jouer',
  'route.title.privacy': 'Confidentialité',
  'route.title.accessibility': 'Accessibilité',
  'route.title.terms': 'Conditions',
  'route.title.developers': 'Développeurs',
  'route.title.connect': 'Connecter votre IA',
  'route.title.support': 'Aide',
  'route.title.about': 'À propos',
  'arcade.heading': 'Les machines',
  'footer.heading': 'Plus de HCC Pinball',
  'footer.machines': 'Les machines',
  'route.title.not-found': 'Introuvable',

  // -------------------------------------------------------------------------
  // Le guide des touches (M3-56, 08 §4.3)
  // -------------------------------------------------------------------------
  'guide.title': 'Commandes',
  'guide.show-title': 'Afficher le rôle des touches',
  'guide.hide-title': 'Masquer les commandes',
  'guide.flipper-left': 'Palet gauche',
  'guide.flipper-right': 'Palet droit',
  'guide.plunger': 'Lanceur',
  'guide.plunger-note':
    'Maintenez, puis relâchez. Plus vous maintenez longtemps, plus la bille part fort.',
  'guide.nudge': 'Coup de hanche',
  'guide.nudge-note': 'Poussez la borne. Trop fort et elle bascule.',
  'guide.pause': 'Pause',
  'keys.shift': 'Maj',
  'keys.space': 'Espace',
  'keys.enter': 'Entrée',
}
